<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { IDropdown } from "@/types/main";
import { ChevronRight, Archive, Check } from "utruckin-icons";

interface ICustomQStatusProps {
  label: string;
  status: string;
  tooltipContent?: string;
  isDisabled?: boolean;
  showActions?: boolean;
  arrow?: boolean;
  items: IDropdown[];
  position: "top" | "bottom";
}

interface ICustomQStatusEmits {
  (e: "next"): void;

  (e: "confirm"): void;

  (e: "on_hold"): void;

  (e: "selected", v: IDropdown): void;
}

const props = withDefaults(defineProps<ICustomQStatusProps>(), {
  label: "",
  status: "",
  isDisabled: false,
  showActions: true,
  arrow: true,
  position: "bottom",
  items: () => [],
});

const emit = defineEmits<ICustomQStatusEmits>();

const isOpen = ref(false);
const customStatusRef = ref<HTMLElement | null>(null);
const listStyle = ref({
  top: "0px",
  left: "0px",
  width: "200px",
  transformOrigin: "top",
});

const displayLabel = computed(() => {
  if (props.label) return props.label;

  const labels: Record<string, string> = {
    pending: "Pending",
    in_progress: "In progress",
    on_hold: "On hold",
    completed: "Completed",
  };

  return labels[props.status];
});

const hasDropdown = computed(() => {
  return props.showActions && ["in_progress"].includes(props.status);
});

const hasArrow = computed(() => {
  return (
    props.showActions &&
    props.arrow &&
    ["pending", "in_progress", "on_hold", "completed"].includes(props.status)
  );
});

const toggle = (e: any) => {
  e.stopPropagation();
  if (props.isDisabled) return;
  if (!props.showActions) return;
  if (!hasDropdown.value) return;
  if (!isOpen.value) {
    updateListPosition();
  }
  isOpen.value = !isOpen.value;
};

function choose(item: IDropdown) {
  emit("selected", item);
  isOpen.value = false;
}

const updateListPosition = () => {
  if (!customStatusRef.value) return;
  const rect = customStatusRef.value.getBoundingClientRect();
  listStyle.value = {
    width: `${rect.width}px`,
    left: `${rect.left + window.scrollX}px`,
    top:
      props.position === "top"
        ? `${rect.top + window.scrollY - 6}px`
        : `${rect.bottom + window.scrollY + 6}px`,
    transformOrigin: props.position === "top" ? "bottom" : "top",
  };
};

const onNext = () => {
  if (props.isDisabled) return;
  emit("next");
};

function onDocumentClick(e: MouseEvent) {
  const el = customStatusRef.value;
  if (!el) return;
  const target = e.target as Node | null;
  if (!target) return;
  if (!el.contains(target)) {
    isOpen.value = false;
  }
}

onMounted(() => {
  document.addEventListener("click", onDocumentClick);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", onDocumentClick);
});
</script>

<template>
  <div class="custom-qa-status" ref="customStatusRef">
    <div class="badge-wrapper">
      <div
        class="badge"
        :class="[status]"
        v-tooltip="{
          content: tooltipContent,
          html: true,
        }"
        @click="toggle"
      >
        <span class="badge__label">{{ displayLabel }}</span>
      </div>
      <template v-if="hasArrow">
        <button
          class="action-btn"
          :class="[status, { disabled: isDisabled }]"
          @click="onNext"
          aria-label="Next driver-status"
        >
          <ChevronRight :size="18" />
        </button>
      </template>
    </div>

    <Teleport to="body">
      <div
        v-if="isOpen"
        class="c-co-dropdown__list"
        :style="{
          position: 'fixed',
          top: listStyle.top,
          left: listStyle.left,
          width: listStyle.width,
          transform: position === 'top' ? 'translateY(-100%)' : 'none',
        }"
      >
        <div
          v-for="(item, index) in items"
          :key="item.value || index"
          class="c-co-dropdown__item"
          @click.stop="choose(item)"
          :class="item.value == 'archive' ? 'archive' : ''"
        >
          <span
            v-if="item.color"
            class="color-dot"
            :style="{ backgroundColor: `var(${item.color})` }"
          />
          <span
            style="display: flex; align-items: center"
            v-if="item.value == 'archive'"
          >
            <Archive :size="16" />
          </span>
          <span class="item-text">{{ item.name }}</span>
          <Check v-if="item.value === modelValue" class="check" />
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style lang="scss" scoped>
.custom-qa-status {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;

  .badge-wrapper {
    display: flex;
    align-items: center;
    gap: 2px;
  }

  .badge,
  .action-btn {
    display: inline-flex;
    align-items: center;
    padding: 4px 8px;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    border-radius: 8px 0 0 8px;

    &__label {
      background: transparent;
      color: var(--color-label);
    }

    &.pending {
      background: var(--color-blue-light);
    }

    &.on_hold {
      background: var(--color-yellow-light);
    }

    &.in_progress {
      background: var(--color-orange-light);
    }

    &.completed {
      background: var(--color-green-light);
      border-radius: 8px;
    }

    &.passed {
      background: var(--color-green-light);
    }

    &.failed {
      background: var(--color-red-light);
    }

    &.skipped {
      background: var(--color-orange-light);
    }
  }

  .action-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border: none;
    color: var(--color-label);
    cursor: pointer;
    border-radius: 0 8px 8px 0;

    &.disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }

  .check-btn {
    background: var(--color-secondary-system-background);
    border-radius: 8px;
  }

  .hold_button {
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: var(--color-secondary-system-background);
    border-radius: 8px;
  }
}
</style>

<style lang="scss">
.c-co-dropdown__list {
  width: max-content !important;

  .c-co-dropdown__item {
    &.archive {
      background: var(--color-red-lighter);
    }

    .item-text {
      color: var(--color-label);
    }
  }
}
</style>
